import WalletCompactLogoBg from './WalletCompactLogoBg.svg';
import WalletLogoBg from './WalletLogoBg.svg';
import WalletLogoDark from './WalletLogoDark.svg';
import WalletLogoLight from './WalletLogoLight.svg';
import { Logo, TLogoAssetSet, TLogoColorModeProp, TLogoSize } from '@verifime/components';

// customer-portal's own Wallet product lockup (two rounded cards plus a cyan WALLET badge),
// declared here rather than inside the shared Logo component: it is a different drawing from
// Logo's Management mark, not a crop of it, and client-portal/admin-portal both consume
// @verifime/components, so the Wallet artwork must stay out of that package (see
// docs/technical-debt.md TD-064 and issue #2264). Only the mechanism (SvgIcon wrapper, size
// steps, viewBox restoration) is shared, via Logo's assetSet prop.
//
// Declares only the four combinations the Wallet artwork actually has: all three colour modes
// for horizontal, onPrimary only for markOnly. There is no stacked layout and no markOnly
// light/dark asset. ClarityLogoProps below turns the other five combinations into a compile
// error rather than the runtime throw Logo raises for an assetSet lookup miss.
export const WALLET_ASSET_SET: TLogoAssetSet = {
  horizontal: {
    icon: {
      light: { component: WalletLogoLight, viewbox: '0 0 112 30' },
      dark: { component: WalletLogoDark, viewbox: '0 0 112 30' },
      onPrimary: { component: WalletLogoBg, viewbox: '0 0 150 40' },
    },
    // Literal pixel heights, not token steps. This is deliberate, not a token-usage violation
    // (ADR-0044/0078 still apply everywhere else): the token scale (24/32/48/64/128px) has no
    // step at 30 or 40, and snapping to the nearest one would resize customer-portal's shipped
    // logo by up to 20% across seventeen screens with no design approval to do so. See the "Note
    // for PR 3" in packages/components/docs/design-decisions/0002-logo-asset-set-as-a-prop.md,
    // and TD-042/TD-060 for the same pre-existing gap elsewhere. medium (40px) is WalletLogoBg's
    // own intrinsic height; small (30px) also lands exactly on WalletLogoLight/Dark's intrinsic
    // height, which is what keeps today's rendering unchanged. small/large otherwise keep the
    // built-in Logo's horizontal ratios (0.75x / 1x / 2x of medium).
    size: { small: '30px', medium: '40px', large: '80px' },
  },
  markOnly: {
    icon: {
      onPrimary: { component: WalletCompactLogoBg, viewbox: '0 0 48 39' },
    },
    // See the horizontal size comment above; same deliberate literal-pixel exception. medium
    // (39px) is WalletCompactLogoBg's own intrinsic height. small/large keep the built-in Logo's
    // markOnly ratios (roughly 0.67x / 1x / 1.33x of medium).
    size: { small: '26px', medium: '39px', large: '52px' },
  },
};

type ClarityLogoHorizontalProps = {
  layout?: 'horizontal';
  colorMode?: TLogoColorModeProp;
  size?: TLogoSize;
};

// The Wallet set has no light or dark markOnly artwork (see WALLET_ASSET_SET above), so this
// branch only ever allows 'onPrimary', making the other three colorMode values unrepresentable at
// the type level rather than a runtime throw from Logo's assetSet lookup.
type ClarityLogoMarkOnlyProps = {
  layout: 'markOnly';
  colorMode?: 'onPrimary';
  size?: TLogoSize;
};

export type ClarityLogoProps = ClarityLogoHorizontalProps | ClarityLogoMarkOnlyProps;

/**
 * customer-portal's Wallet logo, rendered through the shared Logo mechanism (SvgIcon wrapper,
 * size steps, viewBox restoration) with the Wallet asset set declared above. See
 * docs/technical-debt.md TD-064 and issue #2264.
 */
export default function ClarityLogo(props: Readonly<ClarityLogoProps>) {
  if (props.layout === 'markOnly') {
    return (
      <Logo
        assetSet={WALLET_ASSET_SET}
        layout="markOnly"
        colorMode={props.colorMode ?? 'onPrimary'}
        size={props.size}
      />
    );
  }

  return (
    <Logo
      assetSet={WALLET_ASSET_SET}
      layout={props.layout ?? 'horizontal'}
      colorMode={props.colorMode ?? 'auto'}
      size={props.size}
    />
  );
}
